import type { CommerceClient, MarketingItem } from '@altazion/commerce-sdk-core'

import {
  requireString,
  type AltazionModuleDefinition,
  type AltazionSuccessEventKind,
} from '../altazion-engine'

export type AltazionMarketingActionName = 'getItem' | 'getItems'

export type MarketingActionResult = MarketingItem | MarketingItem[]

export const marketingModuleDefinition: AltazionModuleDefinition<'marketing', AltazionMarketingActionName, MarketingActionResult> = {
  moduleName: 'marketing',
  actionAttribute: 'hx-altazion-marketing-action',
  payloadAttributes: {
    'hx-altazion-marketing-code': 'code',
  },
  jsonPayloadAttributes: {
    'hx-altazion-marketing-codes': 'codes',
  },
  parseAction(actionValue: string): AltazionMarketingActionName {
    switch (actionValue) {
      case 'getItem':
      case 'getItems':
        return actionValue
      default:
        throw new Error(`Unsupported marketing action: ${actionValue}`)
    }
  },
  execute(client: CommerceClient, action: AltazionMarketingActionName, payload: Record<string, unknown>): Promise<MarketingActionResult> {
    switch (action) {
      case 'getItem':
        return client.marketing.getItem(requireString(payload.code, 'code'))
      case 'getItems':
        return client.marketing.getItems(readCodes(payload))
    }
  },
  successEventKind(): AltazionSuccessEventKind {
    return 'loaded'
  },
  getResultEventSuffixes(action: AltazionMarketingActionName): string[] {
    switch (action) {
      case 'getItem':
        return ['item-loaded']
      case 'getItems':
        return ['items-loaded']
    }
  },
}

function readCodes(payload: Record<string, unknown>): string[] {
  const value = payload.codes

  if (Array.isArray(value)) {
    const codes = value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)

    if (codes.length > 0) {
      return codes
    }
  }

  if (typeof value === 'string') {
    const codes = value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0)

    if (codes.length > 0) {
      return codes
    }
  }

  if (typeof payload.code === 'string' && payload.code.trim().length > 0) {
    return [payload.code.trim()]
  }

  throw new Error('Missing payload field: codes')
}